/**
 * Single source of truth for the Circuit API base URL + every route the
 * embed widget hits.
 *
 * Consolidated so the SPA, launcher IIFE, and client-log beacon can't drift
 * (they historically each declared their own `API_ENDPOINT` constant, which
 * made env-var wiring easy to break in one place and not notice).
 *
 * The base URL comes from `import.meta.env.CIRCUIT_API_ENDPOINT`, inlined
 * at build time via the explicit `loadEnv` allowlist in both
 * `vite.spa.config.ts` and `vite.launcher.config.ts`. Empty string when
 * unset = same-origin, which is what the dev proxy expects.
 */

/** Base URL for every /chat-bot/* request. Empty string = same-origin. */
export const API_ENDPOINT = import.meta.env.CIRCUIT_API_ENDPOINT ?? '';

export const CHAT_BOT_ROUTES = {
  /** Narrow agent shape (id, name, actions) for the actions pill row. */
  agent: '/chat-bot/agent',
  /** Client-side log intake (window.error, launcher protocol, chat failures). */
  clientLog: '/chat-bot/client-log',
  /** Chatbot config (branding, greeting, agent shape). Fetched by the launcher. */
  config: '/chat-bot/config',
  /** Persisted chat history for the current agent-key session. */
  history: '/chat-bot/history',
  /** Submit thumbs up/down feedback for an assistant message. */
  feedback: '/chat-bot/feedback',
} as const;

/** Convenience: pre-joined absolute URL. */
export function apiUrl(path: string): string {
  return `${API_ENDPOINT}${path}`;
}

/**
 * Session token returned by WebSocket auth and sent back on later connections.
 */
export const SESSION_TOKEN_HEADER = 'X-Session-Token';

/**
 * WebSocket base URL derived from API_ENDPOINT. Empty when
 * API_ENDPOINT is unset (same-origin) — browser callers resolve against
 * `window.location`. `/chat-bot/ws` is the API-key WS handler mounted
 * alongside the existing REST /chat-bot/* routes.
 */
function deriveWSEndpoint(httpEndpoint: string): string {
  if (httpEndpoint.startsWith('https://')) {
    return `wss://${httpEndpoint.slice('https://'.length)}`;
  }
  if (httpEndpoint.startsWith('http://')) {
    return `ws://${httpEndpoint.slice('http://'.length)}`;
  }
  return httpEndpoint;
}

/**
 * Absolute WS URL for `/chat-bot/ws`. Falls back to a same-origin URL
 * derived from `window.location` when API_ENDPOINT is unset so browser
 * consumers (embed, tests) don't need an explicit endpoint.
 */
export function getChatBotWSURL(): string {
  const derived = deriveWSEndpoint(API_ENDPOINT);
  if (derived.length > 0) return `${derived}/chat-bot/ws`;
  if (typeof window === 'undefined') return '/chat-bot/ws';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/chat-bot/ws`;
}
